import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  Users2, 
  Calendar, 
  DollarSign, 
  Settings, 
  LogOut, 
  User, 
  ChevronDown, 
  MoreHorizontal,
  Church,
  FileText,
  BarChart3,
  Users,
  CheckSquare,
  MessageSquare,
  Baby,
  UserPlus,
  ClipboardList,
  Zap,
  BarChart2,
  Heart,
  Bell,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Shield,
  Download,
  Smartphone,
  CreditCard,
  Menu,
  X,
  Search,
  Plus,
  ArrowLeft,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Logo } from '@/components/ui/logo';
import { useAuth } from '@/lib/authContext';
import { isUserAdmin, isSystemAdmin, getApprovalNotifications, getOrganizationName } from '@/lib/data';
import { usePermissions, PERMISSIONS } from '@/lib/permissions.jsx';
import { PermissionNavItem } from '@/components/PermissionGuard';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabaseClient';
import { useIsMobile } from '@/lib/utils/useIsMobile';
// import { MobileDebug } from './MobileDebug';

export function MobileLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSystemAdminUser, setIsSystemAdminUser] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [organizationName, setOrganizationName] = useState('Church App');
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const isMobile = useIsMobile();
  
  // Check if we're in kiosk mode
  const isKioskMode = location.pathname === '/events' && location.search.includes('kiosk=true');

  // Generate navigation items based on user permissions
  const generateNavigation = () => {
    const baseNavigation = [
      { 
        name: 'Home', 
        href: '/dashboard', 
        icon: Home, 
        color: 'text-blue-500',
        permission: PERMISSIONS.REPORTS_VIEW,
        shortName: 'Home'
      },
      { 
        name: 'Events', 
        href: '/events', 
        icon: Calendar, 
        color: 'text-green-500',
        permission: PERMISSIONS.EVENTS_VIEW,
        shortName: 'Events'
      },
      { 
        name: 'People', 
        href: '/members', 
        icon: Users, 
        color: 'text-purple-500',
        permission: PERMISSIONS.MEMBERS_VIEW,
        shortName: 'People'
      },
      { 
        name: 'Check-in', 
        href: '/children-check-in', 
        icon: Baby, 
        color: 'text-pink-500',
        permission: PERMISSIONS.CHILDREN_VIEW,
        shortName: 'Kids'
      },
      { 
        name: 'More', 
        href: '#', 
        icon: MoreHorizontal, 
        color: 'text-gray-500',
        permission: PERMISSIONS.REPORTS_VIEW,
        shortName: 'More',
        isMoreMenu: true
      }
    ];

    const moreNavigation = [
      { 
        name: 'Donations', 
        href: '/donations', 
        icon: DollarSign, 
        color: 'text-yellow-500',
        permission: PERMISSIONS.DONATIONS_VIEW
      },
      { 
        name: 'Tasks', 
        href: '/tasks', 
        icon: ClipboardList, 
        color: 'text-orange-500',
        permission: PERMISSIONS.TASKS_VIEW
      },
      { 
        name: 'Messaging', 
        href: '/messaging', 
        icon: MessageSquare, 
        color: 'text-teal-500',
        permission: PERMISSIONS.SETTINGS_VIEW
      },
      { 
        name: 'Alerts', 
        href: '/alerts', 
        icon: Bell, 
        color: 'text-red-500',
        permission: PERMISSIONS.REPORTS_VIEW
      },
      { 
        name: 'Automations', 
        href: '/automations', 
        icon: Zap, 
        color: 'text-indigo-500',
        permission: PERMISSIONS.SETTINGS_VIEW
      },
      { 
        name: 'Reports', 
        href: '/reports', 
        icon: BarChart2, 
        color: 'text-emerald-500',
        permission: PERMISSIONS.REPORTS_VIEW
      },
      { 
        name: 'Settings', 
        href: '/settings', 
        icon: Settings, 
        color: 'text-gray-500',
        permission: PERMISSIONS.SETTINGS_VIEW
      }
    ];

    // Add Admin Center for system administrators
    if (isSystemAdminUser) {
      moreNavigation.push({
        name: 'Admin Center',
        href: '/admin-center',
        icon: Shield,
        color: 'text-red-600',
        isSystemAdmin: true,
        permission: PERMISSIONS.SYSTEM_ADMIN
      });
    }

    moreNavigation.push({ 
      name: 'Permissions', 
      href: '/permissions', 
      icon: Shield, 
      color: 'text-purple-500',
      permission: PERMISSIONS.USERS_EDIT
    });

    return { baseNavigation, moreNavigation };
  };

  const { baseNavigation, moreNavigation } = generateNavigation();

  // Load admin data
  useEffect(() => {
    const loadAdminData = async () => {
      if (!user) return;
      
      try {
        const [adminStatus, systemAdminStatus] = await Promise.all([
          isUserAdmin(),
          isSystemAdmin()
        ]);
        
        setIsAdmin(adminStatus);
        setIsSystemAdminUser(systemAdminStatus);
        
        if (adminStatus) {
          try {
            const notifications = await getApprovalNotifications();
            setPendingApprovals(notifications.length);
          } catch (error) {
            console.error('Error fetching approval notifications:', error);
            setPendingApprovals(0);
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    const fetchOrganizationName = async () => {
      try {
        const name = await getOrganizationName();
        if (name) {
          setOrganizationName(name);
        }
      } catch (error) {
        console.error('Error fetching organization name:', error);
      }
    };

    loadAdminData();
    fetchOrganizationName();
  }, [user]);

  // Check for impersonation state
  useEffect(() => {
    const checkImpersonationState = () => {
      const impersonatingUser = localStorage.getItem('impersonating_user');
      if (impersonatingUser) {
        setIsImpersonating(true);
        setImpersonationData(JSON.parse(impersonatingUser));
      } else {
        setIsImpersonating(false);
        setImpersonationData(null);
      }
    };

    checkImpersonationState();
    window.addEventListener('storage', checkImpersonationState);
    
    return () => {
      window.removeEventListener('storage', checkImpersonationState);
    };
  }, []);

  // PWA functionality
  useEffect(() => {
    const checkInstallation = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
      setIsInstalled(standalone);
    };

    checkInstallation();

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handlePWAInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const handleSignOut = async () => {
    try {
      setIsImpersonating(false);
      setImpersonationData(null);
      localStorage.clear();
      sessionStorage.clear();
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleSearch = () => setShowSearch(!showSearch);
  const handleOpenAIChat = () => navigate('/ai-chat');

  return (
    <div className="min-h-screen h-[100dvh] max-h-[100dvh] overflow-hidden bg-background flex flex-col">
      {/* Impersonation Banner */}
      {isImpersonating && impersonationData && (
        <div className="bg-amber-100 dark:bg-amber-900/30 border-b border-amber-300 dark:border-amber-700/50 px-4 py-2 sticky top-0 z-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Admin Mode: {impersonationData.admin_name}
              </span>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-200 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-800"
              onClick={() => {
                localStorage.removeItem('impersonating_user');
                setIsImpersonating(false);
                setImpersonationData(null);
                window.location.href = '/login';
              }}
            >
              <Shield className="h-3 w-3 mr-1" />
              Exit
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      {!isKioskMode && (
        <header className="bg-card border-b px-4 py-3 flex justify-between items-center sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              className="p-2"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Logo showText={false} size={32} />
            <div className="flex flex-col">
              <span className="font-semibold text-base text-foreground">BLBC</span>
              <span className="text-xs text-muted-foreground">Deacon</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSearch}
              className="p-2"
            >
              <Search className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenAIChat}
              className="p-2"
              aria-label="Open AI assistant"
            >
              <Sparkles className="h-5 w-5" />
            </Button>
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="text-sm text-muted-foreground">{user?.email}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {!isInstalled && deferredPrompt && (
                  <DropdownMenuItem onClick={handlePWAInstall} className="flex items-center gap-2 text-blue-600">
                    <Download className="h-4 w-4" />
                    Install App
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2 text-red-600">
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
      )}

      {/* Search Overlay */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-16 left-0 right-0 z-50 bg-card border-b p-4"
          >
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search members, events, tasks..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSearch}
                className="p-1"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sidebar Overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={toggleSidebar}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              className="fixed inset-y-0 left-0 w-80 bg-card border-r z-50 flex flex-col"
            >
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Logo showText={false} size={32} />
                    <div>
                      <h2 className="font-semibold text-lg">BLBC</h2>
                      <p className="text-sm text-muted-foreground">Deacon</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleSidebar}
                    className="p-2"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-2">
                  {moreNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <PermissionNavItem key={item.name} permission={item.permission}>
                        <NavLink
                          to={item.href}
                          onClick={toggleSidebar}
                          className={({ isActive }) => cn(
                            "flex items-center gap-3 w-full h-12 px-3 rounded-lg transition-all",
                            isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"
                          )}
                        >
                          <item.icon className={cn("h-5 w-5 flex-shrink-0", isActive ? "text-primary" : item.color)} />
                          <span className="truncate text-base font-medium">{item.name}</span>
                          {item.name === 'Settings' && isAdmin && pendingApprovals > 0 && (
                            <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0">
                              {pendingApprovals}
                            </span>
                          )}
                        </NavLink>
                      </PermissionNavItem>
                    );
                  })}
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain pb-20 [-webkit-overflow-scrolling:touch]">
        <Outlet />
        {/* <MobileDebug /> */}
      </main>

      {/* Bottom Navigation */}
      {!isKioskMode && (
        <nav className="fixed bottom-0 left-0 right-0 bg-card border-t z-40">
          <div className="flex justify-around items-center h-16 px-2">
            {baseNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const isMoreMenu = item.isMoreMenu;
              
              if (isMoreMenu) {
                return (
                  <DropdownMenu key={item.name}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className={cn(
                          "flex flex-col items-center justify-center px-2 py-1 h-auto min-w-0",
                          isActive ? "text-primary" : "text-muted-foreground"
                        )}
                      >
                        <MoreHorizontal className="h-5 w-5 mb-1" />
                        <span className="text-xs font-medium truncate">{item.shortName}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-48 mb-2">
                      {moreNavigation.map((moreItem) => (
                        <PermissionNavItem key={moreItem.name} permission={moreItem.permission}>
                          <DropdownMenuItem asChild>
                            <NavLink
                              to={moreItem.href}
                              className={cn(
                                "flex items-center gap-2 w-full",
                                location.pathname === moreItem.href ? "text-primary" : ""
                              )}
                            >
                              <moreItem.icon className={cn("h-4 w-4", location.pathname === moreItem.href ? "text-primary" : moreItem.color)} />
                              <span>{moreItem.name}</span>
                              {moreItem.name === 'Settings' && isAdmin && pendingApprovals > 0 && (
                                <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {pendingApprovals}
                                </span>
                              )}
                            </NavLink>
                          </DropdownMenuItem>
                        </PermissionNavItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              return (
                <PermissionNavItem key={item.name} permission={item.permission}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) => cn(
                      "flex flex-col items-center justify-center px-2 py-1 h-auto min-w-0 transition-all",
                      isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <item.icon className={cn("h-5 w-5 mb-1", isActive ? "text-primary" : item.color)} />
                    <span className="text-xs font-medium truncate">{item.shortName}</span>
                    {item.name === 'Settings' && isAdmin && pendingApprovals > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                        {pendingApprovals}
                      </span>
                    )}
                  </NavLink>
                </PermissionNavItem>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}